import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import { useI18n } from '../../i18n/I18nContext';
import { popoverRoot, useAnchoredPopover } from '../../hooks/useAnchoredPopover';
import './Dropdown.css';

/**
 * Dropdown — the one select control (spec §3.9). The trigger is a field (`.ui-select`);
 * the menu is a card portaled out of the page (so an `overflow:hidden` card never clips it)
 * and placed under the trigger, or above it when there is more room there.
 *
 *   <Dropdown value={status} onChange={setStatus} options={[{ value: 'ACTIVE', label: 'Faol' }]} ariaLabel="Holat" />
 *
 * options: [{ value, label, separator? }]. The checked option is amber with a check on the
 * right. Keyboard: Enter / Space / Arrow opens; Arrow keys, Home, End and typing a letter
 * move; Enter picks; Esc closes without also closing a dialog the field sits in.
 * Props: value, onChange, options, placeholder, className, ariaLabel, labelledBy, size ('md' | 'sm'),
 * disabled, id.
 *
 * Naming: `ariaLabel` (or a `<label htmlFor={id}>`, or `labelledBy` = the id of a visible label)
 * names the control; the current value stays in the button's description, so a screen reader says
 * "Holat, button, Faol" and never loses what is selected.
 */
export default function Dropdown({
  value,
  onChange,
  options = [],
  placeholder,
  className = '',
  ariaLabel,
  labelledBy,
  size = 'md',
  disabled = false,
  id,
}) {
  const { t } = useI18n();
  const uid = useId();
  const listId = `${uid}-list`;
  const valueId = `${uid}-value`;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const anchorRef = useRef(null);
  const triggerRef = useRef(null);
  const popRef = useRef(null);
  const listRef = useRef(null);
  const typeahead = useRef({ text: '', at: 0 });
  const pos = useAnchoredPopover(open, setOpen, anchorRef, popRef, { matchWidth: true });

  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : null;
  const label = selected?.label || placeholder || t('ui.selectPlaceholder');

  const close = useCallback(() => setOpen(false), []);

  const openMenu = (startAt) => {
    if (disabled) return;
    setActive(startAt != null ? startAt : Math.max(0, selectedIndex));
    setOpen(true);
  };

  const choose = (opt) => {
    onChange(opt.value);
    close();
    if (triggerRef.current) triggerRef.current.focus({ preventScroll: true });
  };

  // Focus the list once it is placed — in the same commit, so the very next key press lands in it.
  const positioned = Boolean(pos);
  useLayoutEffect(() => {
    if (open && positioned && listRef.current) listRef.current.focus({ preventScroll: true });
  }, [open, positioned]);

  // Keep the active option in view while arrowing through a long list.
  useEffect(() => {
    if (!open || active < 0 || !listRef.current) return;
    const el = listRef.current.children[active];
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  const onTriggerKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      openMenu(e.key === 'ArrowUp' ? options.length - 1 : undefined);
    }
  };

  const onListKeyDown = (e) => {
    const last = options.length - 1;
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); setActive((i) => (i >= last ? 0 : i + 1)); return;
      case 'ArrowUp': e.preventDefault(); setActive((i) => (i <= 0 ? last : i - 1)); return;
      case 'Home': e.preventDefault(); setActive(0); return;
      case 'End': e.preventDefault(); setActive(last); return;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (options[active]) choose(options[active]);
        return;
      case 'Tab':
        close();
        return;
      default:
    }
    // Type a letter (or a few, quickly) to jump to the option that starts with it.
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const now = Date.now();
      const buf = typeahead.current;
      buf.text = now - buf.at > 700 ? e.key.toLowerCase() : buf.text + e.key.toLowerCase();
      buf.at = now;
      const from = buf.text.length === 1 ? active + 1 : active;
      const order = [...options.keys()];
      const hit = [...order.slice(from), ...order.slice(0, from)].find((i) => String(options[i].label).toLowerCase().startsWith(buf.text));
      if (hit != null) setActive(hit);
    }
  };

  const menu = open ? (
    <div
      ref={popRef}
      className="ui-menu crm-dropdown__menu"
      style={pos
        ? { top: pos.top, left: pos.left, minWidth: pos.minWidth, zIndex: pos.zIndex }
        : { top: 0, left: 0, opacity: 0, pointerEvents: 'none' }}
    >
      {options.length === 0 ? (
        <div className="ui-menu__empty">{t('ui.noOptionsFound')}</div>
      ) : (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          tabIndex={-1}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabel ? undefined : labelledBy}
          aria-activedescendant={active >= 0 ? `${uid}-opt-${active}` : undefined}
          className="ui-menu__list"
          onKeyDown={onListKeyDown}
        >
          {options.map((opt, i) => {
            const isSelected = opt.value === value;
            const cls = `ui-menu__item crm-dropdown__item${isSelected ? ' crm-dropdown__item--active' : ''}${opt.separator ? ' is-muted crm-dropdown__item--sep' : ''}${i === active ? ' is-focused' : ''}`;
            return (
              <li
                key={String(opt.value)}
                id={`${uid}-opt-${i}`}
                role="option"
                aria-selected={isSelected}
                className={cls}
                onMouseMove={() => { if (active !== i) setActive(i); }}
                onClick={() => choose(opt)}
              >
                <span>{opt.label}</span>
                {isSelected && <Check className="ui-menu__check crm-dropdown__check" aria-hidden="true" />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  ) : null;

  return (
    <div
      className={`crm-dropdown ${className} ${open ? 'crm-dropdown--open' : ''}`.trim()}
      ref={anchorRef}
    >
      <button
        ref={triggerRef}
        id={id}
        type="button"
        className={`crm-dropdown__trigger ui-select${size === 'sm' ? ' ui-select--sm' : ''}${selected ? '' : ' is-placeholder'}`}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={onTriggerKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabel ? undefined : labelledBy}
        aria-describedby={selected && (ariaLabel || labelledBy || id) ? valueId : undefined}
        disabled={disabled}
      >
        <span id={valueId} className="crm-dropdown__label ui-select__label">{label}</span>
        <ChevronDown className="crm-dropdown__chevron ui-select__chevron" aria-hidden="true" />
      </button>
      {menu && createPortal(menu, popoverRoot(anchorRef.current))}
    </div>
  );
}
